import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ModifySlot, SlotItem } from './types';
import {
  TimeBoundaryContext,
  TimeFieldRole,
  adjustTimeSegment,
  formatDisplayValue,
  formatTimeSegments,
  getBoundaryMinutes,
  getDropdownOptions,
  labelToMinutes,
  normalizeTimePart,
  slotItemToMinutes,
  TimeSegment,
} from './time-slot-picker-utils';

export type ScheduleTimeFieldProps = {
  fieldRole: TimeFieldRole;
  slotIndex: number;
  daySlots: ModifySlot[];
  value: SlotItem;
  onChange: (hour: SlotItem['hour'], minute: SlotItem['minute']) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onComplete?: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
  autoFocusOnMount?: boolean;
  onAutoFocusHandled?: () => void;
  disabled?: boolean;
  hasError?: boolean;
};

const ScheduleTimeField: React.FC<ScheduleTimeFieldProps> = ({
  fieldRole,
  slotIndex,
  daySlots,
  value,
  onChange,
  onBlur,
  onFocus,
  onComplete,
  inputRef: inputRefProp,
  autoFocusOnMount = false,
  onAutoFocusHandled,
  disabled = false,
  hasError = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() =>
    formatDisplayValue(value.hour, value.minute),
  );
  const [currentSegment, setCurrentSegment] = useState<TimeSegment>('hour');
  const [digitBuffer, setDigitBuffer] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const localInputRef = useRef<HTMLInputElement>(null);
  const isFocusedRef = useRef(false);

  const setInputElementRef = (element: HTMLInputElement | null) => {
    localInputRef.current = element;
    if (!inputRefProp) {
      return;
    }
    if (typeof inputRefProp === 'function') {
      inputRefProp(element);
      return;
    }
    (inputRefProp as React.MutableRefObject<HTMLInputElement | null>).current = element;
  };

  // Get current hour and minute values
  const getCurrentTime = () => {
    let hourNum: number | null = null;
    let minuteNum: number | null = null;

    if (typeof value.hour === 'number') {
      hourNum = value.hour;
    } else if (typeof value.hour === 'string' && value.hour !== '') {
      hourNum = parseInt(value.hour, 10);
    }

    if (typeof value.minute === 'number') {
      minuteNum = value.minute;
    } else if (typeof value.minute === 'string' && value.minute !== '') {
      minuteNum = parseInt(value.minute, 10);
    }

    const hour =
      hourNum !== null &&
      Number.isInteger(hourNum) &&
      hourNum >= 0 &&
      hourNum <= 23
        ? hourNum
        : 0;
    const minute =
      minuteNum !== null &&
      Number.isInteger(minuteNum) &&
      minuteNum >= 0 &&
      minuteNum <= 59
        ? minuteNum
        : 0;

    return { hour, minute };
  };

  // Update time value and trigger onChange
  const updateTime = (hour: number, minute: number, shouldComplete = false) => {
    const formatted = formatTimeSegments(hour, minute);
    setInputValue(formatted);

    onChange(normalizeTimePart(hour), normalizeTimePart(minute));

    if (shouldComplete) {
      setIsOpen(false);
      onComplete?.();
    }
  };

  const getSegmentParts = (): { hh: string; mm: string } => {
    if (!inputValue) {
      return { hh: '', mm: '' };
    }
    const [hhPart, mmPart] = inputValue.split(':');
    return {
      hh: (hhPart ?? '').trim(),
      mm: (mmPart ?? '').trim(),
    };
  };

  const { hh: hhDisplay, mm: mmDisplay } = getSegmentParts();

  const focusSegment = (segment: TimeSegment) => {
    if (disabled) {
      return;
    }
    setDigitBuffer('');
    setCurrentSegment(segment);
    localInputRef.current?.focus();
  };

  const handleControlMouseDown = (
    e: React.MouseEvent<HTMLElement>,
    segment: TimeSegment,
  ) => {
    if (disabled) {
      return;
    }
    e.preventDefault();
    if (!isFocusedRef.current) {
      isFocusedRef.current = true;
      setIsFocused(true);
      onFocus?.();
      setIsOpen(true);
    }
    focusSegment(segment);
  };

  const boundaryContext: TimeBoundaryContext = useMemo(
    () => ({ fieldRole, slotIndex, daySlots }),
    [fieldRole, slotIndex, daySlots],
  );

  const dropdownOptions = useMemo(() => getDropdownOptions(boundaryContext), [
    boundaryContext,
  ]);

  const { scrollTo } = useMemo(() => getBoundaryMinutes(boundaryContext), [
    boundaryContext,
  ]);

  const currentMinutes = slotItemToMinutes(value);

  // Update display when value changes externally (not during focus)
  useEffect(() => {
    if (isFocusedRef.current) {
      return;
    }
    setInputValue(formatDisplayValue(value.hour, value.minute));
  }, [value.hour, value.minute]);

  useEffect(() => {
    if (!autoFocusOnMount || disabled) {
      return;
    }
    requestAnimationFrame(() => {
      const input = localInputRef.current;
      if (!input) {
        return;
      }
      isFocusedRef.current = true;
      setIsFocused(true);
      onFocus?.();
      input.focus();
      setIsOpen(true);
      setDigitBuffer('');
      setCurrentSegment('hour');
      onAutoFocusHandled?.();
    });
  }, [
    autoFocusOnMount,
    disabled,
    onAutoFocusHandled,
    onFocus,
  ]);

  useEffect(() => {
    if (!isOpen || !listRef.current || dropdownOptions.length === 0) {
      return;
    }
    const scrollTarget =
      dropdownOptions.find((o) => o.minutes === scrollTo)?.minutes ??
      dropdownOptions.find((o) => o.minutes >= scrollTo)?.minutes ??
      dropdownOptions[0].minutes;

    const el = listRef.current.querySelector(
      `[data-minutes="${scrollTarget}"]`,
    ) as HTMLElement;
    el?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, scrollTo, dropdownOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const applyMinutes = useCallback(
    (minutes: number) => {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      onChange(normalizeTimePart(hour), normalizeTimePart(minute));
      setInputValue(formatDisplayValue(hour, minute));
      setIsOpen(false);
      onComplete?.();
    },
    [onChange, onComplete],
  );

  const handleFocus = () => {
    if (!disabled) {
      isFocusedRef.current = true;
      setIsFocused(true);
      onFocus?.();
      setIsOpen(true);
      setDigitBuffer('');
      setCurrentSegment('hour');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const { hour, minute } = getCurrentTime();

    if (e.key >= '0' && e.key <= '9') {
      const digit = e.key;
      const newBuffer = digitBuffer + digit;

      if (currentSegment === 'hour') {
        let newHour: number;

        if (newBuffer.length === 1) {
          newHour = parseInt(newBuffer, 10);
          // If single digit is > 2, it must be complete (e.g., "3" -> "03")
          // Otherwise wait for potential second digit
          if (newHour > 2) {
            updateTime(newHour, minute);
            setDigitBuffer('');
            setCurrentSegment('minute');
          } else {
            setDigitBuffer(newBuffer);
            updateTime(newHour, minute);
          }
        } else if (newBuffer.length === 2) {
          newHour = parseInt(newBuffer, 10);
          if (newHour > 23) {
            // If invalid (>23), start over with just this digit
            newHour = parseInt(digit, 10);
            setDigitBuffer(digit);
            if (newHour > 2) {
              updateTime(newHour, minute);
              setDigitBuffer('');
              setCurrentSegment('minute');
            } else {
              setDigitBuffer(digit);
            }
          } else {
            // Valid two-digit hour, move to minutes
            updateTime(newHour, minute);
            setDigitBuffer('');
            setCurrentSegment('minute');
          }
        } else {
          // Buffer overflow, start fresh
          newHour = parseInt(digit, 10);
          setDigitBuffer(digit);
          updateTime(newHour, minute);
          if (newHour > 2) {
            setDigitBuffer('');
            setCurrentSegment('minute');
          }
        }
      } else {
        // Minute segment logic (similar to hour)
        let newMinute: number;

        if (newBuffer.length === 1) {
          newMinute = parseInt(newBuffer, 10);
          if (newMinute > 5) {
            updateTime(hour, newMinute, true);
            setDigitBuffer('');
          } else {
            setDigitBuffer(newBuffer);
            updateTime(hour, newMinute);
          }
        } else if (newBuffer.length === 2) {
          newMinute = parseInt(newBuffer, 10);
          if (newMinute > 59) {
            newMinute = parseInt(digit, 10);
            setDigitBuffer(digit);
            updateTime(hour, newMinute);
          } else {
            updateTime(hour, newMinute, true);
            setDigitBuffer('');
          }
        } else {
          newMinute = parseInt(digit, 10);
          setDigitBuffer(digit);
          updateTime(hour, newMinute);
          if (newMinute > 5) {
            setDigitBuffer('');
          }
        }
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      const newValue = adjustTimeSegment(
        currentSegment,
        currentSegment === 'hour' ? hour : minute,
        1,
      );
      if (currentSegment === 'hour') {
        updateTime(newValue, minute);
      } else {
        updateTime(hour, newValue);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      const newValue = adjustTimeSegment(
        currentSegment,
        currentSegment === 'hour' ? hour : minute,
        -1,
      );
      if (currentSegment === 'hour') {
        updateTime(newValue, minute);
      } else {
        updateTime(hour, newValue);
      }
      return;
    }

    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      setDigitBuffer('');
      if (e.key === 'ArrowLeft' && currentSegment === 'minute') {
        setCurrentSegment('hour');
      } else if (e.key === 'ArrowRight' && currentSegment === 'hour') {
        setCurrentSegment('minute');
      }
      return;
    }

    if (e.key === 'Tab') {
      // Allow tab navigation
      e.preventDefault();
      if (e.shiftKey && currentSegment === 'minute') {
        setCurrentSegment('hour');
      } else if (!e.shiftKey && currentSegment === 'hour') {
        setCurrentSegment('minute');
      } else {
        // Tab out of component
        setIsOpen(false);
        onComplete?.();
      }
    }

    if (e.key === 'Backspace' || e.key === 'Delete') {
      setDigitBuffer('');
      if (currentSegment === 'hour') {
        updateTime(0, minute);
      } else {
        updateTime(hour, 0);
      }
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false;
    setIsFocused(false);
    setIsOpen(false);
    setDigitBuffer('');
    setCurrentSegment('hour');
    onBlur?.(e);
  };

  const handleOptionMouseDown = (e: React.MouseEvent, label: string) => {
    e.preventDefault();
    applyMinutes(labelToMinutes(label));
    setIsOpen(false);
  };

  return (
    <div
      className={`bs-schedule-time-field${
        hasError ? ' bs-schedule-time-field--error' : ''
      }`}
      ref={containerRef}
    >
      <div
        className={`bs-schedule-time-field__control${
          isFocused ? ' bs-schedule-time-field__control--focused' : ''
        }${disabled ? ' bs-schedule-time-field__control--disabled' : ''}`}
      >
        <input
          ref={setInputElementRef}
          type="text"
          className="bs-schedule-time-field__input regular-2"
          value={inputValue}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleInputBlur}
          disabled={disabled}
          autoComplete="off"
          tabIndex={disabled ? -1 : 0}
          aria-label={fieldRole === 'start' ? 'Start time' : 'End time'}
        />
        <span
          role="button"
          tabIndex={-1}
          aria-label={fieldRole === 'start' ? 'Start hour' : 'End hour'}
          className={`bs-schedule-time-field__segment${
            isFocused && currentSegment === 'hour'
              ? ' bs-schedule-time-field__segment--active'
              : ''
          }${
            !hhDisplay ? ' bs-schedule-time-field__segment--placeholder' : ''
          }`}
          onMouseDown={(event) => handleControlMouseDown(event, 'hour')}
        >
          {hhDisplay || 'hh'}
        </span>
        <span className="bs-schedule-time-field__colon">:</span>
        <span
          role="button"
          tabIndex={-1}
          aria-label={fieldRole === 'start' ? 'Start minute' : 'End minute'}
          className={`bs-schedule-time-field__segment${
            isFocused && currentSegment === 'minute'
              ? ' bs-schedule-time-field__segment--active'
              : ''
          }${
            !mmDisplay ? ' bs-schedule-time-field__segment--placeholder' : ''
          }`}
          onMouseDown={(event) => handleControlMouseDown(event, 'minute')}
        >
          {mmDisplay || 'mm'}
        </span>
      </div>
      {isOpen && dropdownOptions.length > 0 && (
        <ul
          role="listbox"
          className="bs-schedule-time-field__dropdown"
          ref={listRef}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        >
          {dropdownOptions.map(({ label, minutes }) => {
            const isSelected =
              currentMinutes !== null && currentMinutes === minutes;
            return (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              <li
                key={label}
                data-minutes={minutes}
                className={`bs-schedule-time-field__option regular-2${
                  isSelected ? ' bs-schedule-time-field__option--selected' : ''
                }`}
                onMouseDown={(e) => handleOptionMouseDown(e, label)}
              >
                {label.replace(':', ' : ')}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ScheduleTimeField;
